import { Controller } from "@hotwired/stimulus"
import { chime } from "../lair/chime.js"
import { trackEvent } from "../track.js"

const FLIGHT_MS = 1600
const TYPE_STEP_MS = 26

// The story map (tours/map, Lore): the lens flies over the drawn Vale from
// beat to beat, the beat's picture comes up beside it, and Aemvyra's line is
// typed out under it. A tap hurries the line, or moves to the next beat once
// it is said; the way on shows after the last.
export default class extends Controller {
  static targets = ["map", "art", "line", "actions", "more", "skip", "dot"]
  static values = { beats: Array, view: Array }

  connect() {
    this.reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    this.svg = this.mapTarget.querySelector("svg")
    if (!this.svg || this.beatsValue.length === 0) return

    this.svg.inert = true
    const [ width, height ] = this.viewValue
    this.view = { width, height }
    this.ring = this.mountRing()
    this.actionsTarget.hidden = true
    this.lineTarget.textContent = ""
    this.camera = this.boxFor(this.beatsValue[0])
    this.applyCamera(this.camera)

    this.onKey = this.onKey.bind(this)
    this.onResize = () => this.flyTo(this.boxFor(this.beatsValue[this.index]), 0)
    document.addEventListener("keydown", this.onKey)
    window.addEventListener("resize", this.onResize)
    this.mapTarget.addEventListener("click", () => this.advance())

    this.start = setTimeout(() => this.play(0), this.reducedMotion ? 0 : 500)
  }

  disconnect() {
    clearTimeout(this.start)
    clearInterval(this.ticker)
    cancelAnimationFrame(this.frame)
    document.removeEventListener("keydown", this.onKey)
    window.removeEventListener("resize", this.onResize)
  }

  onKey(event) {
    if (![ " ", "Enter", "ArrowRight" ].includes(event.key)) return
    if (event.target.closest?.("button, a, input, textarea")) return
    event.preventDefault()
    this.advance()
  }

  advance() {
    if (this.index === undefined) return
    if (this.ticker) return this.finishLine()
    if (this.index < this.beatsValue.length - 1) this.play(this.index + 1)
  }

  play(index) {
    this.index = index
    const beat = this.beatsValue[index]

    this.dotTargets.forEach((dot, i) => {
      dot.classList.toggle("is-done", i < index)
      dot.classList.toggle("is-here", i === index)
    })
    if (this.hasSkipTarget) this.skipTarget.hidden = index >= this.beatsValue.length - 1
    trackEvent("lore_beat", { beat: beat.key, index })
    this.showArt(beat.key)
    this.markPlace(beat)
    this.flyTo(this.boxFor(beat), this.reducedMotion ? 0 : FLIGHT_MS)
    this.say(beat.line)
  }

  showArt(key) {
    this.artTargets.forEach((art) => {
      const here = art.dataset.beat === key
      art.classList.toggle("ll-lore__art--away", !here)
      art.classList.toggle("ll-lore__art--in", here)
    })
  }

  // A soft ring over the place being spoken of, and its sign lifted.
  markPlace(beat) {
    this.svg.querySelectorAll(".ll-vale-sign--lore").forEach((sign) => sign.classList.remove("ll-vale-sign--lore"))
    if (!beat.place) {
      this.ring.setAttribute("opacity", "0")
      return
    }

    this.svg.querySelector(`.ll-vale-sign[data-vale-sign="${beat.place}"]`)?.classList.add("ll-vale-sign--lore")
    this.ring.setAttribute("transform", `translate(${beat.x} ${beat.y + 14})`)
    this.ring.setAttribute("opacity", "1")
  }

  mountRing() {
    const ns = "http://www.w3.org/2000/svg"
    const ring = document.createElementNS(ns, "g")
    ring.setAttribute("class", "ll-lore-ring")
    ring.setAttribute("opacity", "0")
    for (const delay of [ 0, 1.1 ]) {
      const circle = document.createElementNS(ns, "ellipse")
      circle.setAttribute("rx", "34")
      circle.setAttribute("ry", "17")
      circle.style.animationDelay = `${delay}s`
      ring.appendChild(circle)
    }
    this.svg.appendChild(ring)
    return ring
  }

  // The lens for a beat, in the frame's own aspect. Off-centre so the place
  // sits clear of the picture beside it and the speech box under it.
  boxFor(beat) {
    const rect = this.svg.getBoundingClientRect()
    const aspect = rect.width && rect.height ? rect.width / rect.height : this.view.width / this.view.height
    const phone = rect.width < 768

    if (!beat.place) {
      const width = Math.max(this.view.width, this.view.height * aspect) * (phone ? 0.62 : 1)
      const height = width / aspect
      return { x: (this.view.width - width) / 2, y: (this.view.height - height) / 2 + height * 0.06, width, height }
    }

    const width = this.view.width * beat.zoom * (phone ? 0.7 : 1)
    const height = width / aspect
    const cx = beat.x + (phone ? 0 : width * 0.16)
    const cy = beat.y - height * (phone ? 0.12 : -0.1)
    return { x: cx - width / 2, y: cy - height / 2, width, height }
  }

  flyTo(target, duration) {
    cancelAnimationFrame(this.frame)
    const from = { ...this.camera }
    if (!duration) return this.applyCamera(target)

    const started = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - started) / duration)
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
      // A little rise mid-flight, as if the lens pulled up to look across.
      const lift = Math.sin(Math.PI * t) * 0.12
      const width = from.width + (target.width - from.width) * ease
      const height = from.height + (target.height - from.height) * ease
      const cx = from.x + from.width / 2 + ((target.x + target.width / 2) - (from.x + from.width / 2)) * ease
      const cy = from.y + from.height / 2 + ((target.y + target.height / 2) - (from.y + from.height / 2)) * ease
      const w = width * (1 + lift)
      const h = height * (1 + lift)
      this.applyCamera({ x: cx - w / 2, y: cy - h / 2, width: w, height: h })
      if (t < 1) this.frame = requestAnimationFrame(step)
      else this.applyCamera(target)
    }
    this.frame = requestAnimationFrame(step)
  }

  applyCamera(box) {
    this.camera = box
    this.svg.setAttribute("viewBox", `${box.x.toFixed(1)} ${box.y.toFixed(1)} ${box.width.toFixed(1)} ${box.height.toFixed(1)}`)
  }

  say(text) {
    clearInterval(this.ticker)
    this.moreTarget.hidden = true
    this.actionsTarget.hidden = true
    chime("bubble")

    if (this.reducedMotion) {
      this.lineTarget.textContent = text
      return this.lineDone()
    }

    let shown = 0
    this.lineTarget.textContent = ""
    this.ticker = setInterval(() => {
      shown += 2
      this.lineTarget.textContent = text.slice(0, shown)
      if (shown >= text.length) this.finishLine()
    }, TYPE_STEP_MS)
  }

  finishLine() {
    clearInterval(this.ticker)
    this.ticker = null
    this.lineTarget.textContent = this.beatsValue[this.index].line
    this.lineDone()
  }

  lineDone() {
    const last = this.index >= this.beatsValue.length - 1
    this.moreTarget.hidden = last
    this.actionsTarget.hidden = !last
  }
}
